import { Injectable, InjectionToken, inject } from '@angular/core';
import {
  signUp,
  signIn,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  confirmSignUp,
  resendSignUpCode,
  confirmResetPassword,
  resetPassword,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import type {
  SignUpInput,
  SignInInput,
  ConfirmSignUpInput,
  ConfirmResetPasswordInput,
  ResetPasswordInput,
  FetchAuthSessionOptions,
} from 'aws-amplify/auth';
import { HttpErrorLoggerService } from './http-error-logger.service';

export interface AmplifyAuthFns {
  signUp: typeof signUp;
  signIn: typeof signIn;
  signOut: typeof signOut;
  fetchAuthSession: typeof fetchAuthSession;
  getCurrentUser: typeof getCurrentUser;
  confirmSignUp: typeof confirmSignUp;
  resendSignUpCode: typeof resendSignUpCode;
  confirmResetPassword: typeof confirmResetPassword;
  resetPassword: typeof resetPassword;
}

export const AMPLIFY_AUTH_FNS = new InjectionToken<AmplifyAuthFns>('AMPLIFY_AUTH_FNS', {
  providedIn: 'root',
  factory: () => ({
    signUp,
    signIn,
    signOut,
    fetchAuthSession,
    getCurrentUser,
    confirmSignUp,
    resendSignUpCode,
    confirmResetPassword,
    resetPassword,
  }),
});

@Injectable({
  providedIn: 'root',
})
export class AmplifyAuthService {
  private readonly httpErrorLogger = inject(HttpErrorLoggerService);
  private readonly auth = inject(AMPLIFY_AUTH_FNS);

  signUp(input: SignUpInput) {
    return this.invokeAuthOperation('signUp', () => this.auth.signUp(input));
  }

  signIn(input: SignInInput) {
    return this.invokeAuthOperation('signIn', () => this.auth.signIn(input));
  }

  signOut(options?: { global: boolean }) {
    return this.invokeAuthOperation('signOut', () => this.auth.signOut(options));
  }

  fetchAuthSession(options?: FetchAuthSessionOptions) {
    return this.invokeAuthOperation('fetchAuthSession', () => this.auth.fetchAuthSession(options));
  }

  getCurrentUser() {
    return this.invokeAuthOperation('getCurrentUser', () => this.auth.getCurrentUser());
  }

  confirmSignUp(input: ConfirmSignUpInput) {
    return this.invokeAuthOperation('confirmSignUp', () => this.auth.confirmSignUp(input));
  }

  resendSignUpCode(input: { username: string }) {
    return this.invokeAuthOperation('resendSignUpCode', () => this.auth.resendSignUpCode(input));
  }

  confirmResetPassword(input: ConfirmResetPasswordInput) {
    return this.invokeAuthOperation('confirmResetPassword', () => this.auth.confirmResetPassword(input));
  }

  resetPassword(input: ResetPasswordInput) {
    return this.invokeAuthOperation('resetPassword', () => this.auth.resetPassword(input));
  }

  loadStoredTokens() {
    return cognitoUserPoolsTokenProvider.authTokenStore.loadTokens();
  }

  private invokeAuthOperation<T>(operation: string, fn: () => Promise<T>): Promise<T> {
    return fn().catch((error) => {
      this.httpErrorLogger.logAmplifyAuthError({ operation, error });
      throw error;
    });
  }
}
